// ============================================================================
// USER CAPABILITIES (multi-access)
// is_owner, is_cohost, is_cleaner — replaces role-only and has_team_access checks.
// can_create_properties — any signed-in account may add units they own (Slice 25).
// ============================================================================

import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

export interface UserCapabilities {
  is_owner: boolean;
  is_cohost: boolean;
  is_cleaner: boolean;
  /** Still assigned to clean at least one unit. A leftover cleaners row after Make Team does not count. */
  has_cleaner_assignment: boolean;
  /** True once signed in (not pending_invite). Own units go under Yours. */
  can_create_properties: boolean;
  isLoading: boolean;
}

function resolveCanCreateProperties(opts: { profileRole?: string | null }): boolean {
  if (opts.profileRole === "pending_invite") return false;
  return true;
}

export function useUserCapabilities(): UserCapabilities {
  const { user, profile } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ["user-capabilities", user?.id, profile?.role],
    queryFn: async (): Promise<
      Pick<
        UserCapabilities,
        "is_owner" | "is_cohost" | "is_cleaner" | "has_cleaner_assignment" | "can_create_properties"
      >
    > => {
      if (!user?.id) {
        return {
          is_owner: false,
          is_cohost: false,
          is_cleaner: false,
          has_cleaner_assignment: false,
          can_create_properties: false,
        };
      }

      try {
        const cohortClient = supabase as unknown as {
          from: (table: string) => ReturnType<typeof supabase.from>;
        };

        const [ownedRes, cohostRes, cleanerRes] = await Promise.all([
          supabase
            .from("properties")
            .select("id", { count: "exact", head: true })
            .eq("owner_id", user.id)
            .is("archived_at", null)
            .is("deleted_at", null)
            .limit(1),
          cohortClient
            .from("property_cohosts")
            .select("id", { count: "exact", head: true })
            .eq("cohost_user_id", user.id)
            .not("accepted_at", "is", null)
            .limit(1),
          supabase.from("cleaners").select("id").eq("user_id", user.id).maybeSingle(),
        ]);

        const is_owner = (ownedRes.count ?? 0) > 0;
        const is_cohost = (cohostRes.count ?? 0) > 0;
        const is_cleaner = !!(cleanerRes.data != null);
        let has_cleaner_assignment = false;
        const cleanerId = (cleanerRes.data as { id?: string } | null)?.id;
        if (cleanerId) {
          const assignRes = await supabase
            .from("cleaner_assignments")
            .select("id", { count: "exact", head: true })
            .eq("cleaner_id", cleanerId)
            .limit(1);
          has_cleaner_assignment = (assignRes.count ?? 0) > 0;
        }
        const can_create_properties = resolveCanCreateProperties({
          profileRole: profile?.role,
        });

        return { is_owner, is_cohost, is_cleaner, has_cleaner_assignment, can_create_properties };
      } catch (err) {
        console.error("[useUserCapabilities] Error resolving capabilities:", err);
        return {
          is_owner: false,
          is_cohost: false,
          is_cleaner: false,
          has_cleaner_assignment: false,
          can_create_properties: false,
        };
      }
    },
    enabled: !!user?.id,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 10_000,
  });

  const is_owner = data?.is_owner ?? false;
  const is_cohost = data?.is_cohost ?? false;
  const is_cleaner = data?.is_cleaner ?? false;
  const has_cleaner_assignment = data?.has_cleaner_assignment ?? false;

  return {
    is_owner,
    is_cohost,
    is_cleaner,
    has_cleaner_assignment,
    can_create_properties:
      data?.can_create_properties ??
      resolveCanCreateProperties({
        profileRole: profile?.role,
      }),
    isLoading,
  };
}
